/**
 * Derive L2 client slug from an instance URL (aligned with server escalatedIssuesService).
 * @param {string} raw
 * @returns {string}
 */
export function parseL2ClientNameFromInstanceUrl(raw) {
  if (raw == null || raw === '') return '';
  const str = typeof raw === 'string' ? raw : String(raw);
  const cleaned = str.replace(/<[^>]+>/g, ' ').trim();
  const urlMatch = cleaned.match(/https?:\/\/[^\s<>"']+/i);
  const candidate = urlMatch ? urlMatch[0] : cleaned.trim();

  let u;
  try {
    u = new URL(candidate);
  } catch {
    const m = cleaned.match(/\/([^/?#\s]+)\s*$/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  const host = u.hostname.toLowerCase();
  const hostLabels = host.split('.').filter(Boolean);

  const pathParts = u.pathname
    .split('/')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => decodeURIComponent(p));

  if (pathParts.length > 0) {
    return pathParts[0];
  }

  const isHighqCom =
    hostLabels.length >= 3 &&
    hostLabels[hostLabels.length - 2] === 'highq' &&
    hostLabels[hostLabels.length - 1] === 'com';
  if (isHighqCom && hostLabels.length > 0) return hostLabels[0];
  if (hostLabels.length > 0) return hostLabels[0];
  return '';
}

function extractHttpUrlsFromString(s) {
  if (!s || typeof s !== 'string') return [];
  const out = new Set();
  const hrefMatches = s.matchAll(/href\s*=\s*["'](https?:\/\/[^"']+)["']/gi);
  for (const m of hrefMatches) {
    if (m[1]) out.add(m[1].trim());
  }
  const stripped = s.replace(/<[^>]+>/g, ' ');
  const plain = stripped.match(/https?:\/\/[^\s<>"']+/gi) || [];
  for (let u of plain) {
    u = u.replace(/[.,;)\]]+$/g, '').trim();
    if (u) out.add(u);
  }
  return [...out];
}

/**
 * Split engineering client_name_dummy by comma and semicolon; also splits on colon when surrounded by spaces
 * (avoids breaking `https://` in URLs).
 */
export function splitEscalatedEngineeringClients(raw) {
  if (raw == null || raw === '') return [];
  let s = typeof raw === 'string' ? raw : String(raw);
  s = s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s) return [];
  const chunks = s.split(/\s*[,;]\s*/).map((x) => x.trim()).filter(Boolean);
  const out = [];
  for (const p of chunks) {
    if (/\s:\s/.test(p)) {
      for (const sub of p.split(/\s*:\s*/)) {
        const t = sub.trim();
        if (t) out.push(t);
      }
    } else {
      out.push(p);
    }
  }
  const seen = new Set();
  const uniq = [];
  for (const x of out) {
    const k = x.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      uniq.push(x);
    }
  }
  return uniq;
}

/**
 * Labels for client chips: prefers server `clientNames`, else parses legacy `clientName`.
 * @param {object} item - work item summary
 * @returns {string[]}
 */
export function getEscalatedItemClientLabels(item) {
  if (item == null || item.id == null) return [];
  const fromArr = Array.isArray(item.clientNames)
    ? item.clientNames.map((x) => String(x).trim()).filter(Boolean)
    : [];
  if (fromArr.length > 0) {
    const seen = new Set();
    const out = [];
    for (const x of fromArr) {
      const k = x.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        out.push(x);
      }
    }
    return out;
  }

  const wt = String(item.workItemType || '').trim();
  const raw = item.clientName != null ? String(item.clientName) : '';

  if (/^investigation$/i.test(wt)) {
    if (!raw.trim()) return [];
    const urls = extractHttpUrlsFromString(raw);
    if (urls.length > 1) {
      const seen = new Set();
      const names = [];
      for (const u of urls) {
        const n = parseL2ClientNameFromInstanceUrl(u);
        if (n && !seen.has(n.toLowerCase())) {
          seen.add(n.toLowerCase());
          names.push(n);
        }
      }
      if (names.length > 0) return names;
    }
    const single = parseL2ClientNameFromInstanceUrl(raw);
    return single ? [single] : [];
  }

  return splitEscalatedEngineeringClients(raw);
}

/**
 * Filter rows by Salesforce / Zendesk id, ADO work item ids, or numeric tokens in l1TicketUrl.
 */
export function rowMatchesEscalatedTicketIdSearch(row, q) {
  const needle = String(q).trim().toLowerCase();
  if (!needle) return true;
  const hay = [];
  if (row.userEnteredId != null) hay.push(String(row.userEnteredId));
  (row.investigations || []).forEach((i) => {
    if (i?.id != null) hay.push(String(i.id));
  });
  (row.linkedBugs || []).forEach((b) => {
    if (b?.id != null) hay.push(String(b.id));
  });
  if (row.l1TicketUrl) {
    const u = String(row.l1TicketUrl);
    hay.push(u);
    const digits = u.match(/\d{6,}/g);
    if (digits) digits.forEach((d) => hay.push(d));
  }
  return hay.some((h) => h.toLowerCase().includes(needle));
}
